import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DashboardFilters, DatePreset } from '../types/dashboard.types';
import type { OverviewGranularity } from '@types';
import { DEFAULT_FILTERS, getDateRangeFromPreset } from '../types/dashboard.types';

interface DashboardState {
  filters: DashboardFilters;
  currentPage: number;
  rowsPerPage: number;
  overviewGranularity: OverviewGranularity;
  setFilters: (filters: Partial<DashboardFilters>) => void;
  setDatePreset: (preset: DatePreset) => void;
  setCustomDateRange: (from: Date | null, to: Date | null) => void;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  setOverviewGranularity: (granularity: OverviewGranularity) => void;
  clearFilters: () => void;
  resetPagination: () => void;
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      filters: DEFAULT_FILTERS,
      currentPage: 1,
      rowsPerPage: 20,
      overviewGranularity: 'daily' as OverviewGranularity,

      setFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
          currentPage: 1, // Reset page when filters change
        })); 
      },

      setDatePreset: (preset) => {
        set((state) => {
        // For 'custom', keep existing dates or set to null (user will select via calendar)
          if (preset === 'custom') {
            return {
              filters: {
                ...state.filters,
                dateRange: {
                  preset: 'custom',
                  from: state.filters.dateRange.from,
                  to: state.filters.dateRange.to,
                },
              },
              currentPage: 1,
            };
          }

          const dateRange = getDateRangeFromPreset(preset);
          return {
            filters: {
              ...state.filters,
              dateRange: {
                preset,
                from: dateRange.from,
                to: dateRange.to,
              },
            },
            currentPage: 1,
          };
        }); 
      },

      setCustomDateRange: (from, to) => {
        set((state) => ({
          filters: {
            ...state.filters,
            dateRange: {
              preset: 'custom',
              from,
              to,
            },
          },
          currentPage: 1,
        })); 
      },

      setCurrentPage: (page) => {
        set({ currentPage: page }); 
      },

      setRowsPerPage: (rows) => {
        set({ rowsPerPage: rows, currentPage: 1 }); 
      },

      setOverviewGranularity: (granularity) => {
        set({ overviewGranularity: granularity });
      },

      clearFilters: () => {
        set((state) => ({
          filters: { ...DEFAULT_FILTERS, orgIds: state.filters.orgIds },
          currentPage: 1,
        }));
      },

      resetPagination: () => {
        set({ currentPage: 1 }); 
      },
    }),
    {
      name: 'dashboard-filters-v3',
      partialize: (state) => ({
        filters: state.filters,
        rowsPerPage: state.rowsPerPage,
      }),
    },
  ),
);

export default useDashboardStore;
